import i18nFactory from '@yandex-int/i18n';
import { isUndefined } from 'lodash';
import * as React from 'react';

import { CanvasPcodeVideoFrameParams } from '../../service/Pcode/PcodeCanvasVideo';
import { ResourceType } from '../common/typings';
import { Gallery } from '../Gallery/Gallery';
import { GalleryItem } from '../GalleryItem/GalleryItem';
import {
    CommonGalleryProps,
    GalleryPaneTypes,
    GalleryVideoCollectionItem,
    PreviewVideoItemParams
} from '../GalleryPane/typings';
import {
    GalleryPaneCategoriesFilter,
    GalleryPaneCategoriesFilterProps,
    GalleryPaneNameFilter,
    GalleryPaneNameFilterProps,
    GalleryPaneOrderFilterProps,
    GalleryPaneSemanticTextFilter,
    GalleryPaneSemanticTextFilterProps
} from '../GalleryPaneFilters/GalleryPaneFilters';
import { GalleryPreviewModalPopup } from '../GalleryPreviewModalPopup/GalleryPreviewModalPopup';
import { GalleryPreviewVideo } from '../GalleryPreviewVideo/GalleryPreviewVideo';
import { RecentOrderSelector } from '../RecentOrderSelector/RecentOrderSelector';

import * as keyset from './CanvasVideoGallery.i18n';

const i18n = i18nFactory(keyset);

interface CanvasVideoGalleryFiltersProps
    extends GalleryPaneCategoriesFilterProps,
        GalleryPaneNameFilterProps,
        GalleryPaneOrderFilterProps,
        GalleryPaneSemanticTextFilterProps {}

export interface CanvasVideoGalleryProps extends CanvasVideoGalleryFiltersProps, CommonGalleryProps {
    previewVideoItem: (paneType: GalleryPaneTypes, params?: PreviewVideoItemParams) => void;
    currentPaneCollection: GalleryVideoCollectionItem[];
}

export class CanvasVideoGallery extends React.PureComponent<CanvasVideoGalleryProps> {
    render() {
        const props = {
            ...this.props,
            itemRenderer: this.itemRenderer,
            paneFilterRenderer: this.paneFilterRenderer,
            header: i18n('video-title'),
            resourceType: ResourceType.video
        };

        return (
            <>
                <Gallery {...props} />
                {this.renderModalPreviewPopup()}
            </>
        );
    }

    private renderModalPreviewPopup = () => {
        const { preview, isPopupOpened } = this.props;

        if (!preview || !isPopupOpened) {
            return;
        }

        const { itemSource, hasNext, hasPrev, itemIndex, error } = preview;

        return (
            <GalleryPreviewModalPopup
                header={i18n('video-preview-header')}
                hasPrevious={hasPrev}
                hasNext={hasNext}
                itemCollectionIndex={itemIndex}
                previewItem={this.previewItem}
                applyItem={this.props.applyItem}
                error={error}
            >
                <GalleryPreviewVideo previewSource={itemSource as Partial<CanvasPcodeVideoFrameParams>} />;
            </GalleryPreviewModalPopup>
        );
    };

    private previewItem = (itemIndex?: number): void => {
        const { previewVideoItem, currentPaneType, currentPaneCollection } = this.props;
        if (isUndefined(itemIndex)) {
            previewVideoItem(currentPaneType);
        } else {
            const hasPrev = !isUndefined(currentPaneCollection[itemIndex - 1]);
            const hasNext = !isUndefined(currentPaneCollection[itemIndex + 1]);

            previewVideoItem(currentPaneType, {
                itemIndex,
                hasPrev,
                hasNext
            });
        }
    };

    private getCategoriesFilterButtonText = (count: number): string => {
        return i18n('selected-groups-count', { count });
    };

    private paneFilterRenderer = (paneType: GalleryPaneTypes): React.ReactNode => {
        const {
            recentNameFilter,
            recentOrder,
            changeRecentOrder,
            changeRecentNameFilter,
            selectGalleryCategory,
            fetchGalleryCategoriesData,
            categoriesData,
            selectedGalleryCategories,
            semanticText,
            changeSemanticTextFilter
        } = this.props;

        switch (paneType) {
            case GalleryPaneTypes.recent:
                return (
                    <>
                        <GalleryPaneNameFilter
                            changeRecentNameFilter={changeRecentNameFilter}
                            recentNameFilter={recentNameFilter}
                        />
                        <RecentOrderSelector change={changeRecentOrder} value={recentOrder} />
                    </>
                );

            case GalleryPaneTypes.stock:
                return (
                    <>
                        <GalleryPaneSemanticTextFilter
                            semanticText={semanticText}
                            changeSemanticTextFilter={changeSemanticTextFilter}
                        />
                        <GalleryPaneCategoriesFilter
                            fetchGalleryCategoriesData={fetchGalleryCategoriesData}
                            selectGalleryCategory={selectGalleryCategory}
                            categoriesData={categoriesData}
                            selectedGalleryCategories={selectedGalleryCategories}
                            getButtonText={this.getCategoriesFilterButtonText}
                        />
                    </>
                );

            case GalleryPaneTypes.idea:
            default:
                return;
        }
    };

    private itemRenderer = (index: number): React.ReactNode => {
        const { editItemName, removeItem, applyItem } = this.props;

        const item = this.props.currentPaneCollection[index];

        return (
            <GalleryItem
                key={`gallery-item-${item.id}`}
                thumbnailUrl={item.thumbnailUrl}
                resourceId={item.id}
                name={item.name}
                removable={item.removable}
                applyItem={applyItem}
                removeItem={removeItem}
                previewItem={this.previewItem}
                editItemName={editItemName}
                collectionIndex={index}
            />
        );
    };
}
